import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client'
import { FlyerType } from '@/config/constants';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import peachjarLogo from '@/assets/logo.svg';
import {
  Divider,
  FadeInWrapper,
  HeaderFixed,
  PageTitleHelmet,
} from '@/components';
import Card from './components/Card/Card';
import styles from './Home.styles';
import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import { BodyLarge, } from '@/design-system';
import { ChildrensWakingHours } from './components/ChildrensWakingHours/ChildrensWakingHours';
import { CustomizeYourCommunity } from './components/CustomizeYourCommunity/CustomizeYourCommunity';
import { districtVar, subscriptionsVar } from '@/graphql/state';
import { Institution } from '@/graphql/types/Institution';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import colors from '@/theme/colors';

const DEFAULT_LOGO_SIZE = 32;
const MIN_LOGO_SIZE = 23;

export const Home: React.FC = () => {
  const subscriptions = useReactiveVar(subscriptionsVar);
  const { community } = useContext(UserPreferencesContext);
  const district = useReactiveVar(districtVar);
  const [scrollTop, setScrollTop] = useState(0);
  const trackId = 'home_page';
  const schoolList = useMemo(() => {
    let schools: Institution[] = [];
    subscriptions.forEach(sub => {
      schools.push({
        type: FlyerType.school,
        institutionId: sub.id,
        institutionName: sub.name,
        informations: sub.flyerCounters || [],
      });
    });

    return schools;
  }, [subscriptions])

  const communityList = useMemo(() => ([
  {
    type: FlyerType.community,
    institutionId: 'all',
    institutionName: community.localArea,
    informations: community.flyerCounters || [],
  }]), [community])

  const handler = useCallback(() => {
    setScrollTop(document.body.scrollTop || document.documentElement.scrollTop);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const logoHeigth = useMemo(() => (
    scrollTop <= 10
    ? DEFAULT_LOGO_SIZE
    : Math.abs(scrollTop / 100 - DEFAULT_LOGO_SIZE)
  ), [scrollTop])


  if(!schoolList || !communityList) return <SpinnerOverlay loading />

  return (
    <>
    <PageTitleHelmet>Home</PageTitleHelmet>
    <HeaderFixed>
        <div className={scrollTop >= 800 ? styles.headerShrunk : styles.header}>
          <img
            src={peachjarLogo}
            alt="Peachjar Logo"
            width="101px"
            height={
              logoHeigth > MIN_LOGO_SIZE
                ? `${logoHeigth}px`
                : `${MIN_LOGO_SIZE}px`
            }
            className={styles.logoTransition}
            data-testid="peachjarLogo"
            loading="lazy"
          />
        </div>
       {scrollTop === 0 && (
        <div className={styles.titleContainer}>
          <BodyLarge color={colors.slate}>Thriving Beyond the Classroom</BodyLarge>
        </div>
       )}
        <Divider />
      </HeaderFixed>
      <main className={styles.container} data-testid="homeContainer">
        {schoolList &&
          <Card
            districtId={district?.districtId}
            variant={FlyerType.school}
            list={schoolList}
          />
        }
        {communityList && (
          <Card
            districtId={district?.districtId}
            variant={FlyerType.community}
            list={communityList}
          />
        )}
        <ChildrensWakingHours subscriptions={subscriptions} />
        {!community.isSaved && <CustomizeYourCommunity />}
      </main>
    </>
  );
};

export default Home;
